import { IconButton, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useContext, useEffect, useRef, useState } from 'react';
import CustomTable from './CustomTable';
import { getAllSchools } from '../../api/content-api';
import SnackbarContext from '../../store/snackbar-context';
import AuthContext from '../../store/auth-context';
import Loader from '../../ui/Loader';
import School from '../../model/School';
import { grey } from '@mui/material/colors';
import OlympicsContext from '../../store/olympics-context';
import { filter } from './StagesTable';
import EditSchoolDialog from './EditSchoolDialog';
import { RSPO_VOIVODESHIPS } from '../../constants/rspo-voivodeships';

const voivodeshipLabel = (code: string) => {
	if (!code) return '';
	const found = RSPO_VOIVODESHIPS.find((v) => v.code === code || v.label === code.toLowerCase());
	return found ? found.label : code;
};

const SchoolsTable = () => {
	const [isLoading, setIsLoading] = useState(true);
	const { setMsg } = useContext(SnackbarContext);
	const { token } = useContext(AuthContext);
	const { districtFilterId, searchFilterValue, refreshTablesTrigger, setIsRefreshing } = useContext(OlympicsContext);
	const [schools, setSchools] = useState<School[]>([]);
	const [displayedSchools, setDisplayedSchools] = useState<School[]>([]);
	const [editSchool, setEditSchool] = useState<School | null>(null);
	const isRefreshingRef = useRef(false);

	useEffect(() => {
		setEditSchool(null);
	}, [refreshTablesTrigger]);

	const downloadData = (isRefresh = false) => {
		if (isRefresh) {
			isRefreshingRef.current = true;
			setIsRefreshing(true);
		} else {
			setIsLoading(true);
		}

		getAllSchools(token)
			.then((response) => {
				if (response.status === 200) {
					const schools = response.data.map((s: any) => School.fromApiResponse(s));
					setSchools(schools);
				} else {
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			})
			.catch((exception) => {
				setIsLoading(false);
				console.error(exception);
				setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			});
	};

	useEffect(() => {
		setDisplayedSchools(filterSchools(schools));
	}, [districtFilterId, searchFilterValue, schools]);

	// Load data on mount or when refresh trigger changes
	useEffect(() => {
		if (refreshTablesTrigger === 0) {
			// First load
			downloadData(false);
		} else {
			// Refresh triggered
			downloadData(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshTablesTrigger]);

	const filterSchools = (list: School[]) => {
		let filteredSchools = filter(list, districtFilterId);
		filteredSchools = filteredSchools.filter((s) => {
			if (searchFilterValue !== null && searchFilterValue !== undefined && searchFilterValue !== '') {
				const q = searchFilterValue.toLowerCase();
				if (s.name.toLowerCase().includes(q)) {
					return true;
				}
				if (s.city.toLowerCase().includes(q)) {
					return true;
				}
				if (s.country.toLowerCase().includes(q)) {
					return true;
				}
				if (s.street.toLowerCase().includes(q)) {
					return true;
				}
				if ((s.powiat || '').toLowerCase().includes(q)) {
					return true;
				}
				if ((s.wojewodztwo || '').toLowerCase().includes(q)) {
					return true;
				}
				if (voivodeshipLabel(s.wojewodztwo).toLowerCase().includes(q)) {
					return true;
				}
				return false;
			}
			return true;
		});
		return filteredSchools;
	};

	const data = (input: any) => {
		return {
			columns: [
				{ header: '', accessor: 'edit', align: 'center', width: '44px', bgColor: grey[100], nosort: true },
				{ header: 'Nazwa', accessor: 'name', align: 'left', width: '22%', bgColor: grey[100] },
				{ header: 'Miasto', accessor: 'city', align: 'center', bgColor: grey[100] },
				{ header: 'Kod pocztowy', accessor: 'zipCode', align: 'center', bgColor: grey[100] },
				{ header: 'Ulica', accessor: 'street', align: 'center', bgColor: grey[100] },
				{ header: 'Numer', accessor: 'number', align: 'center', bgColor: grey[100] },
				{ header: 'Okręg', accessor: 'district', align: 'center', bgColor: grey[100] },
				{ header: 'Powiat', accessor: 'powiat', align: 'center', bgColor: grey[100] },
				{ header: 'Województwo', accessor: 'wojewodztwo', align: 'center', bgColor: grey[100] },
				{ header: 'RSPO', accessor: 'rspo', align: 'center', bgColor: grey[100], hidden: true },
			],

			rows: input
				? input.map((i: School) => {
						return {
							edit: (
								<IconButton
									size='small'
									aria-label='Edytuj szkołę'
									onClick={() => setEditSchool(i)}
								>
									<EditOutlinedIcon fontSize='small' />
								</IconButton>
							),
							name: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.name}
								</Typography>
							),
							city: <Typography variant='caption'>{i.cityWithCountry}</Typography>,
							zipCode: <Typography variant='caption'>{i.zipCode}</Typography>,
							street: <Typography variant='caption'>{i.street}</Typography>,
							number: (
								<Typography variant='caption'>
									{i.building + (i.apartment !== '' ? ` / ${i.apartment}` : '')}
								</Typography>
							),
							district: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.district.name}
								</Typography>
							),
							powiat: <Typography variant='caption'>{i.powiat || '—'}</Typography>,
							wojewodztwo: <Typography variant='caption'>{voivodeshipLabel(i.wojewodztwo) || '—'}</Typography>,
							rspo: <Typography variant='caption'>{i.rspo > 0 ? i.rspo : '—'}</Typography>,
						};
					})
				: [],
		};
	};

	return (
		<>
			{isLoading ? (
				<Loader />
			) : (
				<CustomTable
					data={data(displayedSchools)}
					defaultSort={{ accessor: 'name', desc: false }}
				/>
			)}
			<EditSchoolDialog
				open={editSchool !== null}
				school={editSchool}
				onClose={() => setEditSchool(null)}
				onSaved={() => downloadData(true)}
			/>
		</>
	);
};

export default SchoolsTable;
